'use client';

import React, { useState, useEffect } from 'react';
import SearchFilters from './SearchFilters';
import ResearchCard from './ResearchCard';
import CollaborationPortal from './CollaborationPortal';

interface Author {
  name: string;
  affiliation: string;
}

interface ResearchData {
  id: string;
  title: string;
  authors: Author[];
  journal: string;
  year: number;
  category: string;
  methodology: string;
  abstract: string;
  citations: number;
  pdfUrl: string;
  doi: string;
  keyFindings: string[];
}

interface FilterState {
  category: string;
  year: string;
  methodology: string;
}

interface CollaborationFormData {
  name: string;
  email: string;
  institution: string;
  role: string;
  researchArea: string;
  proposalType: string;
  description: string;
}

const ResearchInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<FilterState>({
    category: 'all',
    year: 'all',
    methodology: 'all',
  });
  const [filteredResearch, setFilteredResearch] = useState<ResearchData[]>([]);

  const mockResearch: ResearchData[] = [
    {
      id: '1',
      title: 'The Neuroscience of Forgiveness: Brain Mechanisms Underlying Interpersonal Reconciliation',
      authors: [
        { name: 'Dr. Sarah Chen', affiliation: 'Stanford University' },
        { name: 'Dr. Michael Roberts', affiliation: 'Harvard Medical School' },
      ],
      journal: 'Nature Neuroscience',
      year: 2025,
      category: 'forgiveness',
      methodology: 'quantitative',
      abstract:
        'This groundbreaking study uses fMRI imaging to identify specific neural pathways activated during forgiveness processes. Our research demonstrates that forgiveness engages the prefrontal cortex and reduces amygdala activation, suggesting a neurobiological basis for emotional regulation through forgiveness. The findings provide empirical evidence for forgiveness as a trainable cognitive skill with measurable brain changes.',
      citations: 247,
      pdfUrl: '/research/neuroscience-forgiveness.pdf',
      doi: '10.1038/nn.2025.1234',
      keyFindings: [
        'Forgiveness training increases prefrontal cortex activity by 34%',
        'Reduced amygdala reactivity correlates with improved emotional regulation',
        'Neural plasticity observed after 30-day forgiveness practice',
      ],
    },
    {
      id: '2',
      title: 'Community-Level Peace Building: A Meta-Analysis of Forgiveness Interventions in Post-Conflict Societies',
      authors: [
        { name: 'Dr. Amara Okonkwo', affiliation: 'University of Cape Town' },
        { name: 'Dr. James Wilson', affiliation: 'Oxford University' },
      ],
      journal: 'Journal of Peace Research',
      year: 2024,
      category: 'peace',
      methodology: 'meta',
      abstract:
        'This comprehensive meta-analysis examines 87 forgiveness-based peace-building interventions across 23 post-conflict societies. Results indicate that structured forgiveness programs reduce inter-group hostility by an average of 42% and increase community cohesion scores by 38%. The study identifies key implementation factors that predict successful outcomes in diverse cultural contexts.',
      citations: 189,
      pdfUrl: '/research/community-peace-building.pdf',
      doi: '10.1177/jpr.2024.5678',
      keyFindings: [
        '42% reduction in inter-group hostility after forgiveness interventions',
        'Cultural adaptation increases program effectiveness by 56%',
        'Long-term effects sustained over 5-year follow-up period',
      ],
    },
    {
      id: '3',
      title: 'Trauma Healing Through Collective Forgiveness: Evidence from Rwanda Genocide Survivors',
      authors: [
        { name: 'Dr. Marie Uwimana', affiliation: 'University of Rwanda' },
        { name: 'Dr. David Thompson', affiliation: 'Yale University' },
      ],
      journal: 'Psychological Trauma: Theory, Research, Practice, and Policy',
      year: 2024,
      category: 'trauma',
      methodology: 'mixed',
      abstract:
        'This longitudinal study follows 500 Rwandan genocide survivors participating in community forgiveness programs over three years. Findings reveal significant reductions in PTSD symptoms (63% decrease), improved social functioning, and enhanced community reintegration. The research demonstrates that collective forgiveness processes can facilitate trauma recovery while rebuilding social fabric.',
      citations: 312,
      pdfUrl: '/research/trauma-healing-rwanda.pdf',
      doi: '10.1037/tra0001234',
      keyFindings: [
        '63% reduction in PTSD symptoms after 18 months',
        'Improved social trust scores by 71% in participant communities',
        'Collective forgiveness more effective than individual therapy alone',
      ],
    },
    {
      id: '4',
      title: 'Economic Impact of Peace Festivals: Measuring Social Capital Formation in Divided Communities',
      authors: [
        { name: 'Dr. Elena Rodriguez', affiliation: 'London School of Economics' },
        { name: 'Dr. Ahmed Hassan', affiliation: 'American University of Beirut' },
      ],
      journal: 'World Development',
      year: 2025,
      category: 'community',
      methodology: 'quantitative',
      abstract:
        'This study quantifies the economic and social benefits of annual peace festivals in 15 conflict-affected regions. Results show that communities hosting forgiveness-centered festivals experience 28% increases in inter-group economic cooperation, 45% growth in cross-community business partnerships, and measurable improvements in local economic indicators. The research provides evidence for peace-building as economic development strategy.',
      citations: 156,
      pdfUrl: '/research/economic-impact-peace.pdf',
      doi: '10.1016/j.worlddev.2025.01.012',
      keyFindings: [
        '28% increase in inter-group economic cooperation',
        '45% growth in cross-community business partnerships',
        'Positive ROI of 3.2:1 for peace festival investments',
      ],
    },
    {
      id: '5',
      title: 'Conflict Resolution Through Forgiveness Education: A Randomized Controlled Trial in Schools',
      authors: [
        { name: 'Dr. Lisa Anderson', affiliation: 'University of California, Berkeley' },
        { name: 'Dr. Raj Patel', affiliation: 'University of Toronto' },
      ],
      journal: 'Journal of Educational Psychology',
      year: 2023,
      category: 'conflict',
      methodology: 'quantitative',
      abstract:
        'This RCT involving 2,400 students across 40 schools tests the effectiveness of forgiveness-based conflict resolution curricula. Students receiving the intervention showed 52% reduction in peer conflicts, 67% improvement in empathy scores, and 41% increase in prosocial behaviors. The study provides evidence for integrating forgiveness education into standard school curricula.',
      citations: 203,
      pdfUrl: '/research/forgiveness-education-schools.pdf',
      doi: '10.1037/edu0000789',
      keyFindings: [
        '52% reduction in peer conflicts among intervention students',
        '67% improvement in empathy and perspective-taking abilities',
        'Effects sustained over 2-year follow-up period',
      ],
    },
    {
      id: '6',
      title: 'The Role of Forgiveness in Preventing Violent Extremism: A Qualitative Study',
      authors: [
        { name: 'Dr. Fatima Al-Rashid', affiliation: 'University of Jordan' },
        { name: 'Dr. Thomas Mueller', affiliation: 'Free University of Berlin' },
      ],
      journal: 'Terrorism and Political Violence',
      year: 2024,
      category: 'peace',
      methodology: 'qualitative',
      abstract:
        'Through in-depth interviews with 120 former extremists across 8 countries, this study explores how forgiveness processes contribute to deradicalization. Participants identify forgiveness (both giving and receiving) as critical turning points in their disengagement from violent ideologies. The research offers insights for developing more effective counter-extremism programs.',
      citations: 178,
      pdfUrl: '/research/forgiveness-extremism.pdf',
      doi: '10.1080/tpv.2024.1234567',
      keyFindings: [
        'Forgiveness identified as key factor in 78% of deradicalization cases',
        'Self-forgiveness particularly important for sustained disengagement',
        'Community acceptance through forgiveness reduces recidivism by 64%',
      ],
    },
  ];

  useEffect(() => {
    setIsHydrated(true);
    setFilteredResearch(mockResearch);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    let filtered = mockResearch;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (research) =>
          research.title.toLowerCase().includes(query) ||
          research.abstract.toLowerCase().includes(query) ||
          research.authors.some((author) => author.name.toLowerCase().includes(query))
      );
    }

    if (filters.category !== 'all') {
      filtered = filtered.filter((research) => research.category === filters.category);
    }

    if (filters.year !== 'all') {
      if (filters.year === 'older') {
        filtered = filtered.filter((research) => research.year < 2023);
      } else {
        filtered = filtered.filter((research) => research.year === parseInt(filters.year));
      }
    }

    if (filters.methodology !== 'all') {
      filtered = filtered.filter((research) => research.methodology === filters.methodology);
    }

    setFilteredResearch(filtered);
  }, [searchQuery, filters, isHydrated]);

  const handleSearch = (query: string) => {
    if (!isHydrated) return;
    setSearchQuery(query);
  };

  const handleFilterChange = (newFilters: FilterState) => {
    if (!isHydrated) return;
    setFilters(newFilters);
  };

  const handleDownload = (id: string) => {
    if (!isHydrated) return;
    alert(`Downloading research paper ${id}. In production, this would trigger a PDF download.`);
  };

  const handleCite = (id: string) => {
    if (!isHydrated) return;
    const research = mockResearch.find((r) => r.id === id);
    if (research) {
      const citation = `${research.authors.map((a) => a.name).join(', ')} (${research.year}). ${research.title}. ${research.journal}. DOI: ${research.doi}`;
      alert(`Citation copied to clipboard:\n\n${citation}`);
    }
  };

  const handleCollaborationSubmit = (data: CollaborationFormData) => {
    if (!isHydrated) return;
    alert(
      `Thank you for your collaboration proposal, ${data.name}! Our research team will review your submission and contact you at ${data.email} within 5 business days.`
    );
  };

  if (!isHydrated) {
    return (
      <div className="py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="bg-card rounded-lg shadow-md p-6 mb-8 border border-border">
            <div className="h-12 bg-muted rounded animate-pulse mb-4" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="h-10 bg-muted rounded animate-pulse" />
              <div className="h-10 bg-muted rounded animate-pulse" />
              <div className="h-10 bg-muted rounded animate-pulse" />
            </div>
          </div>
          <div className="space-y-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-card rounded-lg shadow-md p-6 border border-border">
                <div className="h-6 bg-muted rounded animate-pulse mb-4" />
                <div className="h-4 bg-muted rounded animate-pulse mb-2" />
                <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SearchFilters onSearch={handleSearch} onFilterChange={handleFilterChange} />

          <div className="mb-6">
            <p className="text-sm font-body text-muted-foreground">
              Showing {filteredResearch.length} of {mockResearch.length} research papers
            </p>
          </div>

          {filteredResearch.length > 0 ? (
            <div className="space-y-6">
              {filteredResearch.map((research) => (
                <ResearchCard
                  key={research.id}
                  research={research}
                  onDownload={handleDownload}
                  onCite={handleCite}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-lg font-body text-muted-foreground">
                No research papers found matching your criteria. Try adjusting your filters.
              </p>
            </div>
          )}
        </div>
      </div>

      <CollaborationPortal onSubmit={handleCollaborationSubmit} />
    </div>
  );
};

export default ResearchInteractive;